import { tt } from '@agos/shared'

/**
 * Reading a product name the way a cook needs to read it.
 *
 * An order line arrives as one string that the order builder assembled, and it
 * can carry three things at once:
 *
 *   `Margherita (mozzarella, rajčiny) [+ Šunka 2×]`
 *    ^name       ^kitchen description  ^what the customer added
 *
 * The kitchen card needs them apart: the name large, the description as
 * ingredient chips, and the extras merged into those chips with their counts so
 * a cook sees `2× Šunka` rather than "ham" twice in two different places.
 *
 * Ported from legacy `pages/kitchen.tsx`. Pure, and tested, because a parse
 * that loses the brackets loses what the customer paid extra for.
 */

const KOMBO_PREFIX = 'Kombo Pizza:'

export interface ParsedItemName {
  name: string
  /** The parenthesised ingredient list, when the builder put one there. */
  description: string | null
  /** The bracketed additions, verbatim — still one string. */
  extras: string | null
  isKombo: boolean
}

export function parseItemName(productName: string): ParsedItemName {
  if (!productName) return { name: '–', description: null, extras: null, isKombo: false }

  if (productName.startsWith(KOMBO_PREFIX)) {
    const halves = productName
      .slice(KOMBO_PREFIX.length)
      .trim()
      .split('+')
      .map((half) => half.trim())
    return {
      name: 'Kombo Pizza',
      description: halves.join(' · '),
      extras: null,
      isKombo: true,
    }
  }

  let extras: string | null = null
  let cleaned = productName

  const bracketOpen = productName.indexOf('[')
  const bracketClose = productName.lastIndexOf(']')
  if (bracketOpen > 0 && bracketClose > bracketOpen) {
    extras = productName.slice(bracketOpen + 1, bracketClose).trim() || null
    cleaned = productName.slice(0, bracketOpen).trim()
  }

  const parenOpen = cleaned.indexOf('(')
  const parenClose = cleaned.lastIndexOf(')')
  if (parenOpen > 0 && parenClose > parenOpen) {
    return {
      name: cleaned.slice(0, parenOpen).trim(),
      description: cleaned.slice(parenOpen + 1, parenClose).trim() || null,
      extras,
      isKombo: false,
    }
  }

  return { name: cleaned, description: null, extras, isKombo: false }
}

export interface MergedIngredient {
  name: string
  count: number
  /** The customer asked for more of this than the recipe has. */
  boosted: boolean
  /** Not in the recipe at all — an addition. */
  standalone: boolean
}

const MULTIPLIED_EXTRA = /^(.+?)\s+(\d+)×$/

/**
 * Folds the extras into the recipe.
 *
 * A customer who orders extra ham on a pizza that already has ham should see
 * one chip reading `2× Šunka`, not `1× Šunka` in the recipe and `1× Šunka`
 * underneath — the second is how a cook forgets the extra. Matching is loose
 * (either name containing the other) because the recipe says *Šunka* and the
 * topping list says *Šunka pikantná*.
 *
 * Anything that matches nothing stays separate and is marked `standalone`, so
 * the card can colour it as an addition.
 */
export function mergeIngredientsAndExtras(
  description: string | null,
  extras: string | null,
): MergedIngredient[] {
  const base: MergedIngredient[] = []
  if (description) {
    // Both separators are real: the custom-pizza builder writes `·` between the
    // base and its toppings, and the recipe itself uses commas.
    for (const piece of description.split(/[,·]/)) {
      const name = piece.trim()
      if (name) base.push({ name, count: 1, boosted: false, standalone: false })
    }
  }

  const standalone: MergedIngredient[] = []
  if (extras) {
    for (const piece of extras.split(',')) {
      const trimmed = piece.trim()
      if (!trimmed) continue

      const multiplied = MULTIPLIED_EXTRA.exec(trimmed)
      const name = (multiplied?.[1] ?? trimmed).replace(/^\+/, '').trim()
      const count = multiplied ? Number.parseInt(multiplied[2] ?? '1', 10) : 1

      const match = base.find(
        (ingredient) =>
          ingredient.name.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(ingredient.name.toLowerCase()),
      )
      if (match) {
        match.count += count
        match.boosted = true
      } else {
        standalone.push({ name, count, boosted: true, standalone: true })
      }
    }
  }

  return [...base, ...standalone]
}

const PIZZA_SIZE =
  /[\s\-–—]+(\d{2,3})\s?cm|[([\s](\d{2,3})\s?cm[)\]\s,]|^(\d{2,3})\s?cm/i
const PIZZA_SIZE_ANYWHERE = /\b(\d{2,3})\s?cm\b/i

/** `Gazdovská – 32 cm` → `32 cm`. §14.6: the number is not one of two fixed sizes. */
export function extractPizzaSize(productName: string): string | null {
  if (!productName) return null
  const match = PIZZA_SIZE.exec(productName)
  const cm = match?.[1] ?? match?.[2] ?? match?.[3]
  if (cm) return `${cm} cm`
  const fallback = PIZZA_SIZE_ANYWHERE.exec(productName)
  return fallback?.[1] ? `${fallback[1]} cm` : null
}

/**
 * Garlic crust gets its own colour on the card, because it is the one topping
 * that changes what the cook does to the dough rather than what goes on top,
 * and it is the one most often missed. Three spellings are in the data.
 */
export function isGarlicCrust(name: string): boolean {
  const lowered = name.trim().toLowerCase()
  return (
    lowered.includes(tt('cesnakové kraje')) ||
    lowered.includes('cesnak. kraje') ||
    lowered.includes('cesnakove kraje')
  )
}

const GIFT_PREFIX = /^🎁\s*(DARČEK:\s*)?/i

export function isGiftItem(productName: string | undefined): boolean {
  return (productName ?? '').startsWith('🎁')
}

/** `🎁 DARČEK: Kofola` → `Kofola`, for matching against the gift rules. */
export function giftNameOf(productName: string | undefined): string {
  return (productName ?? '').replace(GIFT_PREFIX, '').trim()
}

/**
 * How an ingredient chip is drawn — §9.122, the owner's point 8.
 *
 * *"zvýraznit pizza ingrediencie hrubsie svetlejsie aj počet"*. The board is wall-mounted and read
 * across a kitchen; white at 35 % opacity was the **count**, which is the one thing on the chip that
 * changes what the cook makes.
 *
 * Named tiers rather than literals in the JSX, for the reason §9.55 gives about the amount formatter:
 * a rule that only exists inside a component is a rule nobody can check. The whole of this report is
 * about which tier a piece of text is drawn at, so the tiers are the thing worth testing.
 */
export const CHIP_TIERS = {
  /** Too faint to read at distance. Kept named so a test can assert nothing uses it. */
  muted: 'text-white/35',
  /** An ordinary ingredient. */
  plain: 'text-white/85 bg-white/12',
  /** Its multiplier — brighter than the name was, because the count is what changes the dish. */
  count: 'text-white/70',
  /** Added on top of the recipe, or doubled. */
  boosted: 'text-red-300',
  standalone: 'text-red-300 bg-red-500/20',
  garlic: 'bg-amber-400 text-amber-950',
} as const

export function ingredientChipClasses(state: {
  garlic?: boolean
  standalone?: boolean
}): string {
  if (state.garlic === true) return `${CHIP_TIERS.garlic} font-black`
  if (state.standalone === true) return `${CHIP_TIERS.standalone} font-black`
  return `${CHIP_TIERS.plain} font-black`
}

export function ingredientCountClasses(state: {
  garlic?: boolean
  boosted?: boolean
}): string {
  if (state.garlic === true) return 'font-black text-amber-950'
  return `font-black ${state.boosted === true ? CHIP_TIERS.boosted : CHIP_TIERS.count}`
}
